import { Form, Formik } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { useDialog } from '../../../components/dialog/DialogContext';
import { FILTER_SITES } from "../../../constants/notification-key";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import EnrolmentColumns from "../../../models/ColumnDefinitions/EnrolmentColumns";
import { EventBasedNotification } from "../../../models/EventBasedNotification";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import EnrolmentSearch from "../../../models/search/EnrolmentSearch";
import { hideColumns, hideLookups } from "../../../models/utilities/columnHelper";
import { saveEventBaseNotification } from "../../../services/notificationService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { toast } from "react-toastify";
import CcRecipientStep from "../cc-recipient-step";
import EmailTemplateForm from "../email-step";
import NotificationNameStep from "../notification-name-step";
import NotificationWizard from "../notification-wizard";
import ToRecipientStep from "../to-recipient-step";
import ConfigurationSelectionStep from "./steps/configuration-selection-step";
import EventSelectionStep from "./steps/event-selection-step";
import FilterStep from "./steps/filter-step";
import Summary from "./steps/summary";

interface Props {
    onClose: () => void;
    defaultSteps: WizardSideBarStep[];
    defaultContent: ReactElement[];
    onBack: (event: any) => void;
    setHideMainModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const EventBasedModal: FC<Props> = ({ onClose, defaultSteps, defaultContent, onBack, setHideMainModal }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);    
    const dialog = useDialog();
    const initialItem: EventBasedNotification = {   
        vruleActionId: 0,
        id: 0,
        type: "",
        typeId: "",
        name: "",
        eventSelectionId: [],
        configurationId: [],
        filterBy: FILTER_SITES.eSysnameLookup,
        filterById: FILTER_SITES.sdValstring,
        toRecipients: [],
        ccRecipients: [],
        emailAccount: "",
        emailAccountId: "",
        subject: "",
        body: "",
        disclaimer: translate.byKey(TranslationKeys.dontReplyToThisEmailAddress),
        settingDatasRef: [],
        report: "",
        reportId: "",
        enabled: 0,
        lastTriggered: ""
    };
    const columns = hideLookups(hideColumns(EnrolmentColumns, [
        "totalTags",
        "category",
        "uid",
        "title",
        "totalAccessGroups",
        "gender",
        "suffix",
        "enabled",
        "masterTypeName",
        "site",
    ]), ['name']);
    
    const [sideBarSteps, setSideBarSteps] = useState([
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.name),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.event),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.filter),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.site),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.sendTo),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.copyTo),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.template),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: "",
            substeps: [],
        },
    ]);

    const contentPages: ReactElement[] = [
        ...defaultContent,
        <NotificationNameStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSideBarStepDescription={(newDescription) =>
                setSideBarStepDescription(1, newDescription)
            }
        />,
        <EventSelectionStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSideBarStepDescription={(newDescription) =>
                setSideBarStepDescription(2, newDescription)
            } />,
        <FilterStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSideBarStepDescription={(newDescription: string) =>
                setSideBarStepDescription(3, newDescription)
            }
        />,
        <ConfigurationSelectionStep 
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSideBarStepDescription={(newDescription: string) =>
                setSideBarStepDescription(4, newDescription)
            } 
        />,
        <ToRecipientStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setDescription={(newDescription: string) =>
                setSideBarStepDescription(5, newDescription)
            }
            columnDescriptors={{
                columns: columns,
                searchColumns: EnrolmentSearch,
            }} />,
        <CcRecipientStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setDescription={(newDescription: string) =>
                setSideBarStepDescription(6, newDescription)
            }
            columnDescriptors={{
                columns: columns,
                searchColumns: EnrolmentSearch,
            }} />,
        <EmailTemplateForm
            showPreview={true}
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSideBarStepDescription={(newDescription) => setSideBarStepDescription(7, newDescription)}
            setHideMainModal={setHideMainModal} />,
        <Summary onBack={(event) => onLocalBack(event)} taskDescription={sideBarSteps[0].description}/>,
    ];

    const onLocalNext = (event) => {
        setStep(step + 1);
    };

    const onLocalBack = (event: any) => {
        if (step === 1) {
            onBack(event)
            return;
        }
        setStep(step - 1);
    };

    const setSideBarStepDescription = (index, newDescription) => {
        const newSideBarSteps = sideBarSteps.map((step, stepIndex) => {
            if (index === 3){
                return {
                    ...step,
                    description:
                        stepIndex === index ? newDescription : step.description,
                    title:
                        stepIndex === index + 1 ? newDescription : step.title,
                };
            }
            return {
                ...step,
                description:
                    stepIndex === index ? newDescription : step.description,
            };
        });
        setSideBarSteps(newSideBarSteps);
    };

    const onSaveNotification = (values) => {       
        saveEventBaseNotification(values).then((value) => {
            toast.success(translate.byKey(TranslationKeys.success));
            onClose()
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        })     
    }

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialItem}            
            onSubmit={onSaveNotification}>
            <Form         
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault(); 
                    }
                }
            }>
            {/* This wizard is a special case as we are making a
                 selection between 2 different sets of Wizard steps to use
                 hence why we had to create a separate wizard component rather than
                 using the standard WizardTwo that is used everywhere else */}
                <NotificationWizard
                    sidebarSteps={sideBarSteps}
                    step={step}
                    name={translate.byKey(TranslationKeys.notifications)}
                    onClose={onClose}
                    stepContent={contentPages} />
            </Form>
        </Formik>
    )
}

export default EventBasedModal;
